import React, { useRef, useEffect, useCallback } from 'react';
import * as PIXI from 'pixi.js';

/**
 * PixiWheelCanvas - 高端輪盤 Pixi.js 元件
 *
 * 特色：
 * - 真實物理慣性旋轉動畫
 * - 指針 tick 回饋
 * - 精準角度停止
 * - 中獎粒子特效
 * - 支援動態獎項數
 */


// ===== 常數 =====
const COLORS_DEFAULT = [
  '#FF6B35', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#74b9ff', '#a29bfe',
  '#fd79a8', '#00cec9', '#e17055', '#6c5ce7',
];

function PixiWheelCanvas({ prizes, spinResult, onAnimationComplete }) {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const wheelRef = useRef(null);
  const isSpinningRef = useRef(false);
  const currentAngleRef = useRef(0);
  const particlesRef = useRef([]);

  const getSize = useCallback(() => {
    if (!containerRef.current) return 380;
    return Math.min(containerRef.current.offsetWidth, containerRef.current.offsetHeight, 480);
  }, []);

  // ===== 初始化 Pixi =====
  useEffect(() => {
    if (!containerRef.current || prizes.length === 0) return;

    const size = getSize();

    const app = new PIXI.Application({
      width: size,
      height: size,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    });

    containerRef.current.innerHTML = '';
    containerRef.current.appendChild(app.view);
    app.view.style.width = '100%';
    app.view.style.height = '100%';
    appRef.current = app;

    const center = size / 2;
    const radius = size * 0.42;

    // === 外圈裝飾 ===
    drawOuterRing(app.stage, center, radius);

    // === 輪盤主體 ===
    const wheelContainer = new PIXI.Container();
    wheelContainer.x = center;
    wheelContainer.y = center;
    app.stage.addChild(wheelContainer);
    wheelRef.current = wheelContainer;

    drawWheelSlices(wheelContainer, prizes, radius);

    // === 中心圓 ===
    drawCenterCircle(app.stage, center);

    // === 指針 ===
    drawPointer(app.stage, center, radius);

    // === 外圈小燈 ===
    drawOuterLights(app, center, radius);

    return () => {
      app.destroy(true, { children: true });
      appRef.current = null;
      wheelRef.current = null;
    };
  }, [prizes, getSize]);

  // ===== 處理旋轉動畫 =====
  useEffect(() => {
    if (!spinResult || !wheelRef.current || !appRef.current || isSpinningRef.current) return;

    isSpinningRef.current = true;
    const sliceCount = prizes.length;
    const sliceAngle = (Math.PI * 2) / sliceCount;

    // 計算目標角度：停在對應獎項的中心
    // 指針在頂部（12 點鐘方向 = Pixi 角度 -π/2 即 3π/2）
    // 輪盤順時針旋轉，扇區 i 的中心在本地座標 = i * sliceAngle + sliceAngle/2
    // 旋轉 θ 後扇區出現在 (中心角 + θ)，要對齊指針：θ = 3π/2 - 中心角
    const targetIndex = spinResult.targetIndex;
    const prizeAngle = targetIndex * sliceAngle + sliceAngle / 2;
    const baseTarget = (Math.PI * 3 / 2 - prizeAngle);

    // 加上隨機偏移（在扇區內但不靠太近邊緣）
    const jitter = (Math.random() - 0.5) * sliceAngle * 0.5;

    // 確保至少轉 5~8 圈 + 精確角度
    const extraSpins = (5 + Math.floor(Math.random() * 3)) * Math.PI * 2;
    const targetRotation = currentAngleRef.current + extraSpins + baseTarget -
      (currentAngleRef.current % (Math.PI * 2)) + jitter;

    animateWheel(
      appRef.current,
      wheelRef.current,
      currentAngleRef,
      targetRotation,
      sliceCount,
      () => {
        isSpinningRef.current = false;
        // 中獎特效
        if (spinResult.resultType === 'win') {
          spawnWinParticles(appRef.current, getSize() / 2);
        }
        setTimeout(() => {
          onAnimationComplete();
        }, spinResult.resultType === 'win' ? 1500 : 600);
      }
    );
  }, [spinResult, prizes, onAnimationComplete, getSize]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    />
  );
}

// ===================================================
// 繪圖函數
// ===================================================

function drawOuterRing(stage, center, radius) {
  const ring = new PIXI.Graphics();

  // 外圈陰影
  ring.beginFill(0x1a1a3e, 0.5);
  ring.drawCircle(center, center, radius + 18);
  ring.endFill();

  // 金屬外框
  ring.lineStyle(4, 0xFFD700, 0.8);
  ring.drawCircle(center, center, radius + 12);
  ring.lineStyle(2, 0xffffff, 0.15);
  ring.drawCircle(center, center, radius + 16);
  ring.lineStyle(1, 0xFFD700, 0.3);
  ring.drawCircle(center, center, radius + 6);

  stage.addChild(ring);
}

function drawWheelSlices(container, prizes, radius) {
  const sliceCount = prizes.length;
  const sliceAngle = (Math.PI * 2) / sliceCount;

  for (let i = 0; i < sliceCount; i++) {
    const prize = prizes[i];
    const startAngle = i * sliceAngle;
    const endAngle = startAngle + sliceAngle;
    const color = prize.color
      ? parseInt(prize.color.replace('#', ''), 16)
      : parseInt(COLORS_DEFAULT[i % COLORS_DEFAULT.length].replace('#', ''), 16);

    // 扇形
    const slice = new PIXI.Graphics();
    slice.beginFill(color, 0.85);
    slice.moveTo(0, 0);
    slice.arc(0, 0, radius, startAngle, endAngle);
    slice.lineTo(0, 0);
    slice.endFill();

    // 扇形邊線
    slice.lineStyle(1.5, 0xffffff, 0.15);
    slice.moveTo(0, 0);
    slice.lineTo(
      Math.cos(startAngle) * radius,
      Math.sin(startAngle) * radius
    );

    container.addChild(slice);

    // 文字（沿扇形中心排列）
    const midAngle = startAngle + sliceAngle / 2;
    const textRadius = radius * 0.62;
    const displayName = prize.display_name || prize.displayName || '';

    // icon
    if (prize.icon) {
      const iconText = new PIXI.Text(prize.icon, {
        fontSize: Math.max(16, radius * 0.1),
      });
      iconText.anchor.set(0.5);
      iconText.x = Math.cos(midAngle) * (radius * 0.82);
      iconText.y = Math.sin(midAngle) * (radius * 0.82);
      container.addChild(iconText);
    }

    // 獎項名稱
    const maxChars = sliceCount <= 6 ? 10 : 8;
    const label = displayName.length > maxChars
      ? displayName.slice(0, maxChars) + '..'
      : displayName;

    const text = new PIXI.Text(label, {
      fontSize: Math.max(15, radius * 0.11),
      fontFamily: 'Noto Sans TC, Arial, sans-serif',
      fill: 0xffffff,
      fontWeight: '700',
      align: 'center',
      dropShadow: true,
      dropShadowColor: 0x000000,
      dropShadowBlur: 3,
      dropShadowDistance: 1,
      dropShadowAlpha: 0.5,
    });
    text.anchor.set(0.5);
    text.x = Math.cos(midAngle) * textRadius;
    text.y = Math.sin(midAngle) * textRadius;
    text.rotation = midAngle;
    container.addChild(text);
  }
}

function drawCenterCircle(stage, center) {
  const c = new PIXI.Graphics();

  // 外層光暈
  c.beginFill(0xFFD700, 0.15);
  c.drawCircle(center, center, 36);
  c.endFill();

  // 金屬圓
  c.beginFill(0x1a1a3e);
  c.lineStyle(3, 0xFFD700, 0.9);
  c.drawCircle(center, center, 28);
  c.endFill();

  // 內圈
  c.beginFill(0x0f0c29);
  c.lineStyle(1.5, 0xFFD700, 0.4);
  c.drawCircle(center, center, 20);
  c.endFill();

  // 星形裝飾
  const star = new PIXI.Text('★', {
    fontSize: 18,
    fill: 0xFFD700,
    fontWeight: 'bold',
  });
  star.anchor.set(0.5);
  star.x = center;
  star.y = center;
  stage.addChild(c);
  stage.addChild(star);
}

function drawPointer(stage, center, radius) {
  const pointer = new PIXI.Graphics();
  const pointerY = center - radius - 10;

  // 指針陰影
  pointer.beginFill(0x000000, 0.3);
  pointer.moveTo(center, pointerY + 28);
  pointer.lineTo(center - 13, pointerY - 10);
  pointer.lineTo(center + 13, pointerY - 10);
  pointer.closePath();
  pointer.endFill();

  // 指針本體
  pointer.beginFill(0xFFD700);
  pointer.moveTo(center, pointerY + 24);
  pointer.lineTo(center - 11, pointerY - 12);
  pointer.lineTo(center + 11, pointerY - 12);
  pointer.closePath();
  pointer.endFill();

  // 指針高光
  pointer.beginFill(0xffffff, 0.3);
  pointer.moveTo(center, pointerY + 20);
  pointer.lineTo(center - 5, pointerY - 8);
  pointer.lineTo(center + 2, pointerY - 8);
  pointer.closePath();
  pointer.endFill();

  // 頂端裝飾圓
  pointer.beginFill(0xFFD700);
  pointer.lineStyle(2, 0xffffff, 0.4);
  pointer.drawCircle(center, pointerY - 14, 6);
  pointer.endFill();

  stage.addChild(pointer);
}

function drawOuterLights(app, center, radius) {
  const lightCount = 20;
  const lights = [];

  for (let i = 0; i < lightCount; i++) {
    const angle = (i / lightCount) * Math.PI * 2;
    const x = center + Math.cos(angle) * (radius + 12);
    const y = center + Math.sin(angle) * (radius + 12);

    const light = new PIXI.Graphics();
    light.beginFill(0xFFD700, 0.7);
    light.drawCircle(0, 0, 3);
    light.endFill();
    light.x = x;
    light.y = y;
    light._phase = i;
    app.stage.addChild(light);
    lights.push(light);
  }

  // 燈光閃爍動畫
  let tick = 0;
  app.ticker.add(() => {
    tick += 0.05;
    lights.forEach((light, i) => {
      light.alpha = 0.4 + Math.sin(tick + i * 0.5) * 0.4;
    });
  });
}

// ===================================================
// 動畫引擎
// ===================================================

function animateWheel(app, wheel, currentAngleRef, targetRotation, sliceCount, onComplete) {
  const totalAngle = targetRotation - currentAngleRef.current;
  const duration = 5500; // 5.5 秒
  const startTime = performance.now();
  const startAngle = currentAngleRef.current;
  const sliceAngle = (Math.PI * 2) / sliceCount;

  let lastSliceIndex = Math.floor((currentAngleRef.current % (Math.PI * 2)) / sliceAngle);

  // 音效上下文（Web Audio API）
  let audioCtx = null;
  try {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (e) { /* 靜默失敗 */ }

  function playTick(volume = 0.3) {
    if (!audioCtx) return;
    try {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      osc.frequency.value = 800 + Math.random() * 400;
      osc.type = 'sine';
      gain.gain.value = volume;
      gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.08);
      osc.start();
      osc.stop(audioCtx.currentTime + 0.08);
    } catch (e) { /* ignore */ }
  }

  function playWinSound() {
    if (!audioCtx) return;
    try {
      [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.value = 0.35;
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + i * 0.15 + 0.4);
        osc.start(audioCtx.currentTime + i * 0.15);
        osc.stop(audioCtx.currentTime + i * 0.15 + 0.3);
      });
    } catch (e) { /* ignore */ }
  }

  /**
   * 自訂緩動函數 - 模擬真實輪盤物理
   * 階段1 (0~0.08): 快速加速（ease-in）
   * 階段2 (0.08~0.35): 高速旋轉
   * 階段3 (0.35~1.0): 逐漸減速（阻尼衰減）+ 最後的微彈跳
   */
  function wheelEasing(t) {
    if (t < 0.08) {
      // 加速：cubic ease-in
      const p = t / 0.08;
      return 0.08 * (p * p * p);
    } else if (t < 0.35) {
      // 高速均勻
      return 0.08 + (t - 0.08) * (0.45 / 0.27);
    } else {
      // 減速：指數衰減 + 微彈跳
      const p = (t - 0.35) / 0.65;
      const decel = 1 - Math.pow(1 - p, 3.5);
      // 接近停止時的阻尼擺動
      const damping = Math.exp(-6 * p) * Math.sin(p * 3) * 0.005;
      return 0.53 + (0.47) * decel + damping;
    }
  }

  function tick() {
    const now = performance.now();
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const easedProgress = wheelEasing(progress);
    const currentAngle = startAngle + totalAngle * easedProgress;

    wheel.rotation = currentAngle;
    currentAngleRef.current = currentAngle;

    // Tick 音效: 每經過一個扇區
    const currentSliceIndex = Math.floor(
      ((currentAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / sliceAngle
    );
    if (currentSliceIndex !== lastSliceIndex) {
      lastSliceIndex = currentSliceIndex;
      // 音量隨速度遞減
      const speed = 1 - progress;
      playTick(0.15 + speed * 0.35);
    }

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      // 停止 - 微小回彈效果
      const bounceAmplitude = 0.015;
      const bounceDuration = 300;
      const bounceStart = performance.now();

      function bounce() {
        const bt = (performance.now() - bounceStart) / bounceDuration;
        if (bt >= 1) {
          wheel.rotation = targetRotation;
          currentAngleRef.current = targetRotation;
          playWinSound();
          onComplete();
          return;
        }
        const offset = Math.sin(bt * Math.PI * 2) * bounceAmplitude * (1 - bt);
        wheel.rotation = targetRotation + offset;
        requestAnimationFrame(bounce);
      }
      bounce();
    }
  }

  requestAnimationFrame(tick);
}

// ===================================================
// 中獎粒子特效
// ===================================================

function spawnWinParticles(app, center) {
  if (!app || !app.stage) return;
  const particleCount = 40;

  for (let i = 0; i < particleCount; i++) {
    const particle = new PIXI.Graphics();
    const colors = [0xFFD700, 0xffffff, 0xff6b6b, 0x4ECDC4, 0xff9ff3];
    const color = colors[Math.floor(Math.random() * colors.length)];
    const size = 2 + Math.random() * 4;

    particle.beginFill(color, 0.9);
    if (Math.random() > 0.5) {
      particle.drawCircle(0, 0, size);
    } else {
      particle.drawRect(-size / 2, -size / 2, size, size);
    }
    particle.endFill();

    particle.x = center;
    particle.y = center;

    const angle = Math.random() * Math.PI * 2;
    const speed = 2 + Math.random() * 5;
    particle._vx = Math.cos(angle) * speed;
    particle._vy = Math.sin(angle) * speed - 2;
    particle._life = 1;
    particle._decay = 0.01 + Math.random() * 0.02;

    app.stage.addChild(particle);

    const animate = () => {
      particle._vx *= 0.98;
      particle._vy += 0.08; // 重力
      particle._vy *= 0.98;
      particle.x += particle._vx;
      particle.y += particle._vy;
      particle._life -= particle._decay;
      particle.alpha = particle._life;
      particle.scale.set(particle._life);

      if (particle._life > 0 && app.stage) {
        requestAnimationFrame(animate);
      } else if (app.stage) {
        app.stage.removeChild(particle);
        particle.destroy();
      }
    };

    // 錯開粒子發射時間
    setTimeout(() => requestAnimationFrame(animate), Math.random() * 200);
  }
}

export default PixiWheelCanvas;
